import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';

interface SectionHeroProps {
  name: string;
  subtitle: string;
  icon: string;
  color: string;
  glow: string;
  bg: string;
  image: string;
  totalCount: number;
  subcategoryCount: number;
}

const SectionHero = ({ name, subtitle, icon, color, glow, bg, image, totalCount, subcategoryCount }: SectionHeroProps) => {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.05, rootMargin: '0px' });

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className="relative overflow-hidden rounded-2xl mb-10"
      style={{
        minHeight: '340px',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: 'opacity 0.6s ease, transform 0.6s ease',
      }}
    >
      {/* Background image */}
      <img
        src={image}
        alt={name}
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      {/* Overlays */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(5,5,8,0.92) 0%, rgba(5,5,8,0.7) 50%, rgba(5,5,8,0.4) 100%)' }} />
      <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 20% 50%, ${glow} 0%, transparent 60%)` }} />

      {/* Top glow line */}
      <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} />

      {/* Content */}
      <div className="relative z-10 px-8 md:px-14 py-12 flex flex-col md:flex-row items-start md:items-center gap-8">
        {/* Left: icon + text */}
        <div className="flex-1">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-5 font-mono text-xs">
            <Link to="/" className="transition-colors cursor-pointer" style={{ color: 'rgba(255,255,255,0.35)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = color; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.35)'; }}
            >Главная</Link>
            <i className="ri-arrow-right-s-line" style={{ color: 'rgba(255,255,255,0.2)' }} />
            <Link to="/catalog" className="transition-colors cursor-pointer" style={{ color: 'rgba(255,255,255,0.35)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = color; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.35)'; }}
            >Каталог</Link>
            <i className="ri-arrow-right-s-line" style={{ color: 'rgba(255,255,255,0.2)' }} />
            <span style={{ color }}>{name}</span>
          </div>

          {/* Icon + title */}
          <div className="flex items-center gap-4 mb-4">
            <div
              className="w-16 h-16 flex items-center justify-center rounded-2xl shrink-0"
              style={{ background: bg, border: `1px solid ${color}50`, boxShadow: `0 0 24px ${glow}` }}
            >
              <i className={`${icon} text-3xl`} style={{ color, filter: `drop-shadow(0 0 8px ${color})` }} />
            </div>
            <div>
              <h1
                className="font-orbitron font-black text-3xl md:text-4xl leading-tight"
                style={{ color, textShadow: `0 0 20px ${glow}` }}
              >
                {name}
              </h1>
              <p className="font-mono text-sm mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>{subtitle}</p>
            </div>
          </div>

          {/* Stats row */}
          <div className="flex flex-wrap items-center gap-4 mt-6">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full font-mono text-xs" style={{ background: `${color}12`, border: `1px solid ${color}35`, color }}>
              <i className="ri-store-2-line" />
              <span>{totalCount.toLocaleString('ru-RU')}+ товаров</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full font-mono text-xs" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}>
              <i className="ri-grid-line" />
              <span>{subcategoryCount} подкатегорий</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full font-mono text-xs" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}>
              <i className="ri-truck-line" />
              <span>Доставка 2 часа</span>
            </div>
          </div>
        </div>

        {/* Right: CTA */}
        <div className="flex flex-col gap-3 shrink-0">
          <Link
            to={`/catalog?section=${encodeURIComponent(name)}`}
            className="whitespace-nowrap flex items-center gap-2 px-6 py-3 rounded-full font-mono text-xs tracking-widest cursor-pointer transition-all duration-300"
            style={{ background: color, color: '#050508', fontWeight: 700, boxShadow: `0 0 20px ${glow}` }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 0 32px ${glow}`; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 0 20px ${glow}`; }}
          >
            <i className="ri-shopping-bag-line" />
            ВСЕ ТОВАРЫ РАЗДЕЛА
          </Link>
          <Link
            to="/catalog"
            className="whitespace-nowrap flex items-center justify-center gap-2 px-6 py-3 rounded-full font-mono text-xs tracking-widest cursor-pointer transition-all duration-300"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.5)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#fff'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.3)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.12)'; }}
          >
            <i className="ri-arrow-left-line" />
            ВЕСЬ КАТАЛОГ
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SectionHero;
